<script lang="ts">
export default {
  name: 'PdfCanvas',
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick, onUnmounted } from 'vue';
import { fabric } from 'fabric';
import * as pdfjsLib from 'pdfjs-dist';
import type { ParsedField } from '@/types/field';
import { Loader, AlertCircle, FileText, ChevronLeft, ChevronRight } from '@lucide/vue';

// Configure PDF.js worker - use worker from public directory
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const props = defineProps<{
  pdfUrl: string | null;
  selectedField: ParsedField | null;
  selectionTrigger?: number;
}>();

const emit = defineEmits<{
  error: [message: string];
}>();

const canvasContainer = ref<HTMLDivElement>();
const scrollContainer = ref<HTMLDivElement>();
const pages = ref<fabric.Canvas[]>([]);
const currentHighlight = ref<fabric.Rect | null>(null);
const pageCanvases = ref<HTMLCanvasElement[]>([]);
const pageViewports = ref<Array<{ width: number; height: number; scale: number }>>([]);
const pageWrappers = ref<HTMLDivElement[]>([]);
const isLoading = ref(false);
const loadError = ref<string>('');
const currentPage = ref(1);

const totalPages = computed(() => pages.value.length);

const loadPdf = async (url: string) => {
  if (!canvasContainer.value) {
    console.error('Canvas container not ready');
    return;
  }

  isLoading.value = true;
  loadError.value = '';
  currentPage.value = 1;

  try {
    // Clear existing canvases
    pages.value.forEach((canvas: fabric.Canvas) => canvas.dispose());
    pages.value = [];
    pageCanvases.value = [];
    pageViewports.value = [];
    pageWrappers.value = [];
    canvasContainer.value.innerHTML = '';

    // Fetch PDF from URL
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF: ${response.statusText}`);
    }
    const arrayBuffer = await response.arrayBuffer();

    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    const pageWidth = 700; // Slightly smaller to fit in 2/3 column

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });

      // Calculate scale to fit page width
      const scale = pageWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      // Create canvas element for PDF page
      const canvas = document.createElement('canvas');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      canvas.dataset.pageNumber = String(pageNum);
      canvas.style.display = 'block';
      canvas.style.width = `${scaledViewport.width}px`;
      canvas.style.height = `${scaledViewport.height}px`;
      canvas.style.backgroundColor = 'white';
      canvas.style.margin = '0';
      canvas.style.padding = '0';

      // Render PDF page to canvas
      const context = canvas.getContext('2d');
      if (context) {
        await page.render({
          canvasContext: context,
          canvas: canvas,
          viewport: scaledViewport,
        }).promise;
      }

      // Create Fabric.js canvas overlay for highlighting
      const fabricCanvasEl = document.createElement('canvas');
      fabricCanvasEl.width = scaledViewport.width;
      fabricCanvasEl.height = scaledViewport.height;
      fabricCanvasEl.style.width = `${scaledViewport.width}px`;
      fabricCanvasEl.style.height = `${scaledViewport.height}px`;
      fabricCanvasEl.style.margin = '0';
      fabricCanvasEl.style.padding = '0';

      // Create wrapper div with relative positioning
      const wrapper = document.createElement('div');
      wrapper.style.position = 'relative';
      wrapper.style.display = 'inline-block';
      wrapper.style.marginBottom = '1rem';
      wrapper.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
      wrapper.style.width = `${scaledViewport.width}px`;
      wrapper.style.height = `${scaledViewport.height}px`;
      wrapper.dataset.pageIndex = String(pageNum - 1);
      wrapper.appendChild(canvas);
      wrapper.appendChild(fabricCanvasEl);

      canvasContainer.value?.appendChild(wrapper);

      const fCanvas = new fabric.Canvas(fabricCanvasEl, {
        width: scaledViewport.width,
        height: scaledViewport.height,
        selection: false,
        renderOnAddRemove: true,
      });

      // Position Fabric wrapper absolutely on top
      const lowerCanvas = fCanvas.getElement();
      const upperCanvas = fCanvas.getSelectionElement();
      const fabricWrapper = lowerCanvas?.parentElement;
      if (fabricWrapper) {
        fabricWrapper.style.position = 'absolute';
        fabricWrapper.style.top = '0';
        fabricWrapper.style.left = '0';
        fabricWrapper.style.width = `${scaledViewport.width}px`;
        fabricWrapper.style.height = `${scaledViewport.height}px`;
        fabricWrapper.style.pointerEvents = 'none';
        fabricWrapper.style.zIndex = '10';
      }

      if (lowerCanvas) {
        lowerCanvas.style.pointerEvents = 'none';
      }
      if (upperCanvas) {
        upperCanvas.style.pointerEvents = 'none';
      }

      fCanvas.setDimensions({
        width: scaledViewport.width,
        height: scaledViewport.height,
      });

      pages.value.push(fCanvas);
      pageCanvases.value.push(canvas);
      pageWrappers.value.push(wrapper);
      pageViewports.value.push({
        width: scaledViewport.width,
        height: scaledViewport.height,
        scale: scale,
      });
    }

    isLoading.value = false;
    await nextTick();
  } catch (error) {
    console.error('Error loading PDF:', error);
    loadError.value = `Failed to load PDF: ${error instanceof Error ? error.message : 'Unknown error'}`;
    emit('error', loadError.value);
    isLoading.value = false;
  }
};

const highlightField = (field: ParsedField) => {
  // Remove previous highlight
  if (currentHighlight.value) {
    const pageCanvas = pages.value[currentHighlight.value.data?.pageIndex as number];
    if (pageCanvas) {
      pageCanvas.remove(currentHighlight.value);
      pageCanvas.renderAll();
    }
    currentHighlight.value = null;
  }

  // Skip if field has no location
  if (!field.location) {
    return;
  }

  const pageIndex = field.location.page - 1;
  const pageCanvas = pages.value[pageIndex];
  const htmlCanvas = pageCanvases.value[pageIndex];

  // Extraction sometimes emits page 0 (or a page past the end) for a value it could not place.
  // That is a data quirk, not an app fault, so warn rather than error (errors reach Sentry).
  if (!pageCanvas || !htmlCanvas) {
    console.warn(`Page canvas not found for page ${field.location.page}`);
    return;
  }

  const htmlCanvasWidth = htmlCanvas.width;
  const htmlCanvasHeight = htmlCanvas.height;

  // Use fabricjs coordinates (normalized 0-1 range)
  const { left, top, width, height } = field.location.fabricjs;

  // Convert normalized coordinates to canvas pixels
  const rectLeft = left * htmlCanvasWidth;
  const rectTop = top * htmlCanvasHeight;
  const rectWidth = width * htmlCanvasWidth;
  const rectHeight = height * htmlCanvasHeight;

  const rect = new fabric.Rect({
    left: rectLeft,
    top: rectTop,
    width: rectWidth,
    height: rectHeight,
    fill: 'transparent',
    stroke: 'red',
    strokeWidth: 2,
    selectable: false,
    evented: false,
  });

  rect.data = { pageIndex };
  pageCanvas.add(rect);
  pageCanvas.renderAll();
  currentHighlight.value = rect;

  // Scroll to the field - calculate precise position to center the highlight
  const wrapper = pageWrappers.value[pageIndex];
  if (wrapper && scrollContainer.value) {
    // Get wrapper's position relative to the scroll container's content
    const wrapperOffsetTop = wrapper.offsetTop;

    // Calculate the center of the highlight within the page
    const highlightCenterY = rectTop + rectHeight / 2;

    // Calculate target scroll position to center the highlight in the viewport
    const containerHeight = scrollContainer.value.clientHeight;
    const targetScrollTop = wrapperOffsetTop + highlightCenterY - containerHeight / 2;

    scrollContainer.value.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth',
    });
  }
};

// Handle scroll to detect current visible page
const handleScroll = () => {
  if (!scrollContainer.value || pageWrappers.value.length === 0) return;

  const containerRect = scrollContainer.value.getBoundingClientRect();
  const containerCenter = containerRect.top + containerRect.height / 2;

  let closestPage = 1;
  let closestDistance = Infinity;

  pageWrappers.value.forEach((wrapper, index) => {
    const wrapperRect = wrapper.getBoundingClientRect();
    const wrapperCenter = wrapperRect.top + wrapperRect.height / 2;
    const distance = Math.abs(containerCenter - wrapperCenter);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestPage = index + 1;
    }
  });

  currentPage.value = closestPage;
};

// Navigation functions
const goToPage = (pageNum: number) => {
  if (pageNum < 1 || pageNum > totalPages.value) return;

  const wrapper = pageWrappers.value[pageNum - 1];
  if (wrapper) {
    wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    goToPage(currentPage.value - 1);
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    goToPage(currentPage.value + 1);
  }
};

// Watch for PDF URL changes
watch(
  () => props.pdfUrl,
  async (newUrl) => {
    if (newUrl) {
      await nextTick();
      let retries = 0;
      while (!canvasContainer.value && retries < 10) {
        await nextTick();
        retries++;
      }
      if (canvasContainer.value) {
        await loadPdf(newUrl);
      } else {
        loadError.value = 'Failed to initialize canvas container';
        emit('error', loadError.value);
      }
    } else {
      // Clear the container when PDF is removed
      if (canvasContainer.value) {
        canvasContainer.value.innerHTML = '';
      }
      pages.value.forEach((canvas: fabric.Canvas) => canvas.dispose());
      pages.value = [];
      pageCanvases.value = [];
      pageViewports.value = [];
      pageWrappers.value = [];
      currentPage.value = 1;
    }
  },
  { immediate: false }
);

// Watch for selected field changes (including re-selection of same field via trigger)
watch(
  [() => props.selectedField, () => props.selectionTrigger],
  ([newField]) => {
    if (newField && pages.value.length > 0) {
      highlightField(newField);
    }
  }
);

onMounted(async () => {
  if (props.pdfUrl) {
    await nextTick();
    let retries = 0;
    while (!canvasContainer.value && retries < 10) {
      await nextTick();
      retries++;
    }
    if (canvasContainer.value) {
      await loadPdf(props.pdfUrl);
    }
  }
});

onUnmounted(() => {
  pages.value.forEach((canvas: fabric.Canvas) => canvas.dispose());
  pages.value = [];
});
</script>

<template>
  <div class="flex h-full min-h-0 flex-col overflow-hidden">
    <!-- Empty State -->
    <div
      v-if="!pdfUrl"
      class="flex flex-1 flex-col items-center justify-center text-slate-400">
      <FileText class="mb-4 h-12 w-12" />
      <p>Select a result to view the document</p>
    </div>

    <!-- Error State -->
    <div
      v-else-if="loadError"
      class="flex flex-1 flex-col items-center justify-center text-red-500">
      <AlertCircle class="mb-4 h-12 w-12" />
      <p>{{ loadError }}</p>
    </div>

    <!-- PDF Content -->
    <div
      v-else
      class="relative min-h-0 flex-1 overflow-hidden">
      <!-- Scrollable PDF Area -->
      <div
        ref="scrollContainer"
        @scroll="handleScroll"
        class="h-full overflow-y-auto bg-slate-200 p-4">
        <!-- Loading Overlay -->
        <div
          v-if="isLoading"
          class="absolute inset-0 z-20 flex items-center justify-center bg-slate-200/90">
          <div class="flex flex-col items-center gap-2">
            <Loader class="h-8 w-8 animate-spin text-blue-600" />
            <span class="text-slate-600">Loading PDF...</span>
          </div>
        </div>

        <!-- Canvas Container -->
        <div
          ref="canvasContainer"
          class="flex flex-col items-center gap-4"></div>
      </div>

      <!-- Pagination Overlay -->
      <div
        v-if="totalPages > 0 && !isLoading"
        class="absolute bottom-4 left-1/2 z-10 -translate-x-1/2">
        <div class="flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 shadow-lg backdrop-blur-sm">
          <button
            @click="prevPage"
            :disabled="currentPage <= 1"
            class="rounded-full p-1 text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40">
            <ChevronLeft class="h-4 w-4" />
          </button>
          <span class="min-w-20 text-center text-sm font-medium text-white">
            {{ currentPage }} / {{ totalPages }}
          </span>
          <button
            @click="nextPage"
            :disabled="currentPage >= totalPages"
            class="rounded-full p-1 text-white transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40">
            <ChevronRight class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
